const POSPrint = ({
  tabletMode,
  cart,
  reciptDetails,
  totalPrice,
  orderType,
  orderId,
  mobile,
  tenderedAmount,
  selectedDiscount,
  apiResponsePrint,
}) => {
  const safeJsonParse = (value) => {
    if (!value) return value;
    if (typeof value !== "string") return value;

    try {
      const parsed = JSON.parse(value);

      if (typeof parsed === "string") {
        try {
          return JSON.parse(parsed);
        } catch {
          return parsed;
        }
      }

      return parsed;
    } catch {
      return value;
    }
  };

  const getRawCustomization = (item) => {
    const raw =
      item?.customization ??
      item?.customizations ??
      item?.customizationData ??
      item?.customization_details ??
      item?.customize ??
      [];

    return safeJsonParse(raw);
  };

  const getComboCustomization = (item) => {
    const directLineType = String(item?.lineType || item?.line_type || "")
      .trim()
      .toUpperCase();

    if (directLineType === "COMBO" || item?.comboId || item?.combo_id) {
      return item;
    }

    const parsed = getRawCustomization(item);

    if (
      parsed &&
      !Array.isArray(parsed) &&
      typeof parsed === "object" &&
      String(parsed?.lineType || parsed?.line_type || "")
        .trim()
        .toUpperCase() === "COMBO"
    ) {
      return parsed;
    }

    if (Array.isArray(parsed)) {
      return (
        parsed.find(
          (entry) =>
            String(entry?.lineType || entry?.line_type || "")
              .trim()
              .toUpperCase() === "COMBO",
        ) || null
      );
    }

    return null;
  };

  const parseCustomizations = (item) => {
    const parsed = getRawCustomization(item);

    if (Array.isArray(parsed)) {
      return parsed.filter(
        (entry) =>
          String(entry?.lineType || entry?.line_type || "")
            .trim()
            .toUpperCase() !== "COMBO",
      );
    }

    if (getComboCustomization(item)) {
      return [];
    }

    if (parsed && typeof parsed === "object") {
      return [parsed];
    }

    return [];
  };

  const getCustomizationText = (item) => {
    return parseCustomizations(item)
      .flatMap((customization) => {
        const options =
          customization?.options ??
          customization?.option ??
          customization?.addons ??
          customization?.values ??
          customization?.value ??
          customization?.selectedOptions ??
          [];

        if (Array.isArray(options)) {
          return options;
        }

        if (typeof options === "string") {
          const parsedOptions = safeJsonParse(options);

          if (Array.isArray(parsedOptions)) {
            return parsedOptions;
          }

          return options ? [options] : [];
        }

        if (options && typeof options === "object") {
          return [options];
        }

        return [];
      })
      .map((option) => {
        if (typeof option === "string") {
          return option;
        }

        if (option && typeof option === "object") {
          return (
            option?.name ||
            option?.option_name ||
            option?.item_name ||
            option?.label ||
            option?.value ||
            ""
          );
        }

        return "";
      })
      .filter(Boolean)
      .join(", ");
  };

  const getComboSelectedGroups = (item) => {
    const combo = getComboCustomization(item);

    return Array.isArray(combo?.selectedGroups) ? combo.selectedGroups : [];
  };

  const getComboPrintHTML = (item) => {
    const combo = getComboCustomization(item);

    if (!combo) return "";

    const description = combo?.comboDescription
      ? `<div class="custom-text">${combo.comboDescription}</div>`
      : "";

    const groups = getComboSelectedGroups(item);

    const groupsHtml = groups
      .map((group) => {
        const groupTitle = group?.groupTitle || "Selection";

        const selectedItems = (Array.isArray(group?.items) ? group.items : [])
          .map((selectedItem) => {
            const name =
              selectedItem?.productName ||
              selectedItem?.actualName ||
              selectedItem?.itemName ||
              "Item";

            const size =
              selectedItem?.sizeWords || selectedItem?.variantName || "";

            return size ? `${name} (${size})` : name;
          })
          .filter(Boolean)
          .join(", ");

        if (!selectedItems) return "";

        return `<div class="custom-text"><b>${groupTitle}:</b> ${selectedItems}</div>`;
      })
      .filter(Boolean)
      .join("");

    return `${description}${groupsHtml}`;
  };

  const handlePrintAPI = (reciptDetails = {}, item, fullAddress) => {
    const getStoredReceiptDetails = () => {
      try {
        const raw = sessionStorage.getItem("pos_latest_receipt_details");
        return raw ? JSON.parse(raw) : {};
      } catch (error) {
        console.error("Stored receipt parse error:", error);
        return {};
      }
    };

    const storedReceiptDetails = getStoredReceiptDetails();

    const receiptSourceRaw = Array.isArray(reciptDetails?.response)
      ? reciptDetails.response[0]
      : Array.isArray(reciptDetails)
        ? reciptDetails[0]
        : reciptDetails || {};

    const incomingBusinessName =
      receiptSourceRaw?.business_name ||
      receiptSourceRaw?.businessName ||
      receiptSourceRaw?.organization_name ||
      receiptSourceRaw?.restaurant_name ||
      receiptSourceRaw?.shop_name ||
      receiptSourceRaw?.name ||
      "";

    const validIncomingBusinessName =
      incomingBusinessName &&
      String(incomingBusinessName).trim().toLowerCase() !== "restaurant"
        ? incomingBusinessName
        : "";

    const receiptSource = {
      ...receiptSourceRaw,

      business_name:
        validIncomingBusinessName || storedReceiptDetails?.business_name || "",

      address: receiptSourceRaw?.address || storedReceiptDetails?.address || "",

      phone_no:
        receiptSourceRaw?.phone_no || storedReceiptDetails?.phone_no || "",

      gst: receiptSourceRaw?.gst || storedReceiptDetails?.gst || "",

      taxCgst:
        receiptSourceRaw?.taxCgst || storedReceiptDetails?.taxCgst || "0",

      taxSgst:
        receiptSourceRaw?.taxSgst || storedReceiptDetails?.taxSgst || "0",

      tag_line1:
        receiptSourceRaw?.tag_line1 ||
        storedReceiptDetails?.tag_line1 ||
        "Visit Again",
    };

    const receipt = {
      business_name:
        receiptSource?.business_name ||
        receiptSource?.businessName ||
        receiptSource?.organization_name ||
        receiptSource?.restaurant_name ||
        receiptSource?.shop_name ||
        receiptSource?.name ||
        item?.business_name ||
        item?.restaurant_name ||
        item?.shop_name ||
        "Restaurant",

      address:
        receiptSource?.address ||
        receiptSource?.restaurant_address ||
        receiptSource?.organization_address ||
        receiptSource?.business_address ||
        receiptSource?.shop_address ||
        receiptSource?.full_address ||
        receiptSource?.store_address ||
        receiptSource?.branch_address ||
        receiptSource?.billing_address ||
        item?.restaurant_address ||
        item?.shop_address ||
        item?.address ||
        "",

      phone_no:
        receiptSource?.phone_no ||
        receiptSource?.phoneNo ||
        receiptSource?.phone ||
        receiptSource?.mobile ||
        receiptSource?.mobile_no ||
        receiptSource?.mobileNo ||
        receiptSource?.restaurant_mobile ||
        receiptSource?.organization_mobile ||
        receiptSource?.shop_mobile ||
        receiptSource?.contact_number ||
        receiptSource?.contactNo ||
        receiptSource?.contact_no ||
        receiptSource?.business_mobile ||
        item?.restaurant_mobile ||
        item?.shop_mobile ||
        item?.phone_no ||
        "",

      gst:
        receiptSource?.gst ||
        receiptSource?.gst_no ||
        receiptSource?.gstNo ||
        receiptSource?.gst_number ||
        receiptSource?.gstNumber ||
        item?.gst_no ||
        item?.gst ||
        "",

      fssaiNo:
        receiptSource?.fssaiNo ||
        receiptSource?.fssai_no ||
        receiptSource?.fssai ||
        receiptSource?.fssai_number ||
        item?.fssai_no ||
        item?.fssaiNo ||
        "",

      taxCgst:
        receiptSource?.taxCgst ||
        receiptSource?.tax_cgst ||
        receiptSource?.cgstRate ||
        item?.tax_cgst_rate ||
        item?.cgstRate ||
        "0",

      taxSgst:
        receiptSource?.taxSgst ||
        receiptSource?.tax_sgst ||
        receiptSource?.sgstRate ||
        item?.tax_sgst_rate ||
        item?.sgstRate ||
        "0",

      tag_line1:
        receiptSource?.tag_line1 ||
        receiptSource?.tagLine1 ||
        receiptSource?.tagline1 ||
        "Visit Again",
    };

    const receiptUserId = String(
      receiptSource?.userid ||
        receiptSource?.user_id ||
        receiptSource?.restaurant_userid ||
        receiptSource?.restaurantUserId ||
        storedReceiptDetails?.userid ||
        "",
    ).trim();

    const normalizedGST = String(receipt?.gst || "")
      .trim()
      .toUpperCase();

    const showFssai =
      receiptUserId === "A1222" || normalizedGST === "09AADCV8451C1Z9";

    console.log("POS FSSAI CHECK:", {
      receiptUserId,
      normalizedGST,
      showFssai,
    });

    let formattedAddress = "Not available";

    try {
      const addressObj = JSON.parse(item.address_json);
      const values = addressObj?.values || {};

      formattedAddress = [
        values.name,
        values.house_number,
        values.address,
        values.landmark_area,
        values.city,
        values.state,
        values.in_pin_code,
        `Ph: ${values.phone_number}`,
      ]
        .filter(Boolean)
        .join(", ")
        .replace(/,/g, ",&nbsp;");
    } catch (error) {
      console.error("Failed to parse address_json:", error);
    }

    const singleReceiptSection = `
      <div class="container">
        <div class="header">
       <p class="textSizeLarge" style="font-weight: 600;">${receipt.business_name}</p>
${receipt.address ? `<p class="textSizeMid">${receipt.address}</p>` : ""}
${receipt.phone_no ? `<p class="textSizeMid">PH: ${receipt.phone_no}</p>` : ""}
${receipt.gst ? `<p class="textSizeMid">GST NO: ${receipt.gst}</p>` : ""}
${item.fssai_no ? `<p class="textSizeMid">FSSAI NO: ${item.fssai_no}</p>` : ""}
          <p class="textSizeLarge" style="font-weight: 600;">** INVOICE **</p>
        </div>

        <div class="header2">
          <p class="textSizeMid">Order No: ${
            item.daily_order_id
          } / ${item.orderid}</p>
          <p class="textSizeMid">Order Type: ${item.orderType}</p>

          ${
            !(
              item.customer_name?.toLowerCase() === "default" &&
              (item.customer_mobile === "+91 9999999999" ||
                item.customer_mobile === "+91999999999" ||
                item.customer_mobile === "91999999999" ||
                item.customer_mobile === "999999999")
            )
              ? `
            <p class="textSizeMid">Customer Name: ${item.customer_name}</p>
            <p class="textSizeMid">Customer Mobile: ${item.customer_mobile}</p>
          `
              : ""
          }

          <p class="textSizeMid">Payment Status: ${item.payment_status}</p>  
          <p class="textSizeMid">Payment Mode: ${item.payment_mode}</p>
          <p class="textSizeMid">Order Date: ${item.order_date}</p>

          ${
            item.orderType === "homedelivery"
              ? `<p class="textSizeMid">Delivery Address: ${formattedAddress}</p>`
              : ""
          }

          ${
            item.orderType === "dining"
              ? `<p class="textSizeMid">Table No: ${item.table_no}</p>`
              : ""
          }

          ${
            item.orderType === "room_order"
              ? `<p class="textSizeMid">Room No: ${item.table_no}</p>`
              : ""
          }
        </div>

        <div class="headerTable">
          <table class="order-details" style="border-collapse: collapse;">
            <thead>
              <tr>
                <th class="table-text-center" style="padding-right:5px;">Sr.</th>
                <th class="table-text-center">ITEM</th>
                <th class="table-text-center">QTY</th>
                <th class="table-text-center" style="padding-left:5px; padding-right:5px;">PRC.</th>
                <th class="table-text-center">TTL.</th>
              </tr>
            </thead>
            <tbody>
              ${item.items
                .map((subitem, index) => {
                  const combo = getComboCustomization(subitem);
                  const normalCustomizationText = getCustomizationText(subitem);

                  const customizationText = combo
                    ? getComboPrintHTML(subitem)
                    : normalCustomizationText
                      ? `<div class="custom-text">(${normalCustomizationText})</div>`
                      : "";

                  const displayItemName =
                    combo?.comboName ||
                    subitem?.item_name ||
                    subitem?.productName ||
                    "Item";

                  return `
                    <tr>
                      <td class="table-text-center">${1 + index}</td>
                      <td class="table-text-center">
                        ${displayItemName}
                        ${customizationText}
                      </td>
                      <td class="table-text-center" style="padding-left:5px;">
                        ${subitem.quantity}
                      </td>
                      <td class="table-text-center" style="padding-left:5px; padding-right:5px;">
                        ${parseFloat(subitem.item_price)}
                      </td>
                      <td class="table-text-center">
                        ${parseFloat(subitem.total_price)}
                      </td>
                    </tr>
                  `;
                })
                .join("")}
            </tbody>
          </table>
        </div>

        <div class="headerTotal">
          <div class="flex-row">
            <p class="textSizeMid" style="margin: 0px;">Sub Total :</p>
            <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${
              item.subtotal
            }</p>
          </div>

          ${
            item.discount_amount !== "0.00"
              ? `
            <div class="flex-row">
              <p class="textSizeMid" style="margin: 0px;">Discount Amount (${
                item.discount_percentage
              }%) :</p>
              <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${Number(
                item.discount_amount,
              ).toFixed(2)}</p>
            </div>
          `
              : ""
          }

          ${
            item.coupon_applied
              ? `
            <div class="flex-row">
              <p class="textSizeMid" style="margin: 0px;">Coupon discount :</p>
              <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${Number(
                item.coupon_code_discount,
              ).toFixed(2)}</p>
            </div>
          `
              : ""
          }

          ${
            Number(reciptDetails.taxCgst) > 0 ||
            Number(reciptDetails.taxSgst) > 0
              ? `
            <div class="flex-row">
              <p class="textSizeMid" style="margin: 0px;">CGST (${parseFloat(
                reciptDetails?.taxCgst || "0",
              ).toFixed(1)}%) :</p>
              <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${Number(
                item.tax_cgst,
              ).toFixed(2)}</p>
            </div>
            <div class="flex-row">
              <p class="textSizeMid" style="margin: 0px;">SGST (${parseFloat(
                reciptDetails?.taxSgst || "0",
              ).toFixed(1)}%) :</p>
              <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${Number(
                item.tax_sgst,
              ).toFixed(2)}</p>
            </div>
          `
              : ""
          }

          ${
            item.orderType === "homedelivery"
              ? `
            <div class="flex-row">
              <p class="textSizeMid" style="margin: 0px;">Delivery Fee :</p>
              <p class="textSizeMid" style="margin: 0px; text-align: right;">Rs. ${
                item.delivery_fee
              }</p>
            </div>
          `
              : ""
          }

          <div class="flex-row font-weight-bold">
            <p class="textSizeMid" style="margin: 0px;font-weight: 600;">Total Amount :</p>
            <p class="textSizeMid" style="margin: 0px; text-align: right; font-weight: 600;">Rs. ${
              item.totalOrderPrice
            }</p>
          </div>
          
        </div>

        <div class="header2">
          <p class="textSizeMid">Net Amount : Rs. ${item.totalOrderPrice}</p>
          <p class="textSizeMid">Paid Amount : Rs. ${item.pay_amount}</p>
          <p class="textSizeMid">Due Amount : Rs. ${item.due_amount}</p>
        </div>

        <div class="headerEnd">
          <p class="textSizeMid">Thank you for your purchase!</p>
          <p class="textSizeSmall">* ${reciptDetails?.tag_line1} *</p>
        </div>
      </div>
    `;

    const customerReceiptHTML = `
      <html>
        <head>
          <style>
            @page {
              size: 80mm;
              margin: 0;
            }
            body {
              margin: 0;
              margin-right:15px;
              font-family: Arial, sans-serif;
              font-size: 16px;
              color: black;
            }
            .header {
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              text-align: center;
              color: #000;
              line-height: 1.1;
              border-bottom: 1px dashed #000;
              margin-top: 0px;
            }
            .header2 {
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              text-align: start;
              color: #000;
              line-height: 1.5;
              border-bottom: 1px dashed #000;
              margin-top: 0px;
              padding-bottom: 0px;
            }
            .headerTable {
              width: 100%;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              text-align: start;
              color: #000;
              line-height: 1.2;
              border-bottom: 1px dashed #000;
              padding: 0;
              margin: 0;
            }
            .headerTotal {
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              text-align: left;
              color: #000;
              line-height: 1.5;
              border-bottom: 1px dashed #000;
              padding-bottom: 5px;
              padding-top: 5px;
              padding:0px;
            }
            .flex-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
              width: 100%;
            }
            .headerEnd {
              text-align: center;
              color: #000;
              padding-bottom: 0px;
            }
            .textSizeLarge {
              font-size: 16px;
              margin: 1px;
              padding: 1px;
            }
            .textSizeMid {
              font-size: 12px;
              margin: 0px;
              padding: 0px;
            }
            .textSizeSmall {
              font-size: 9px;
              margin: 1px;
              padding: 1px;
            }
            .order-details { width: 100%; border-collapse: collapse; }
            .order-details th,
            .order-details td {
              padding: 0px;
              padding-top: 5px;
              padding-bottom: 5px;
              font-size: 12px;
              text-align: left;
              vertical-align: top;
            }
            .table-text-center { text-align: center; }
            .custom-text {
              font-size: 10px;
              line-height: 1.25;
              margin-top: 2px;
              font-weight: 400;
              text-align: left;
            }
            .page-break {
              page-break-after: always;
            }
          </style>
        </head>
        <body>
          ${singleReceiptSection}
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(customerReceiptHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <>
      <button
        id="pos-print-btn"
        onClick={() => {
          if (Array.isArray(apiResponsePrint)) {
            if (apiResponsePrint.length > 0) {
              handlePrintAPI(reciptDetails, apiResponsePrint[0], "");
            } else {
              console.error("apiResponsePrint is an empty array.");
            }
          } else if (
            typeof apiResponsePrint === "object" &&
            apiResponsePrint !== null
          ) {
            handlePrintAPI(reciptDetails, apiResponsePrint, "");
          } else {
            console.error("apiResponsePrint is not valid.");
          }
        }}
        style={{ height: "120px", width: "250px" }}
        className={
          tabletMode
            ? "vm-tablet-button scale-button shadow border border-secondary cursor-pointer rounded d-flex justify-content-center align-items-center"
            : "scale-button shadow border border-secondary m-2 cursor-pointer rounded bg-gray d-flex justify-content-center align-items-center fs-4 fw-bold"
        }
      >
        PRINT
      </button>
    </>
  );
};

export default POSPrint;
